import { Reducer } from 'redux';
import { CLEAR_PRIO_CACHE } from '../../../actions';
import {
  CREATE_USER_SETTING_COMMIT,
  CREATE_USER_SETTING_ROLLBACK,
  FETCH_SETTING_COMMIT,
  FETCH_SETTING_REQUEST,
  FETCH_SETTING_ROLLBACK,
  UPDATE_USER_SETTING_COMMIT,
  UPDATE_USER_SETTING_ROLLBACK,
} from '../actions/setting';
import {
  ESettingScope,
  ESettingScopeType,
  Setting,
} from '../../../models/Settings';
import { PhoneLinePreference } from '../../../models/Types';
import { distinctByProperty } from '../../../util';

const SCOPE_PRIORITY: Record<ESettingScopeType, number> = {
  [ESettingScope.NONE]: 0,
  [ESettingScope.GLOBAL]: 1,
  [ESettingScope.OFFICE]: 2,
  [ESettingScope.PROJECT]: 3,
  [ESettingScope.PROJECTEXTENSION]: 4,
  [ESettingScope.USER]: 5,
};

const getSettingWithHighestPriority = (settings: Setting[]): Setting => {
  if (settings.length === 0) {
    return null;
  }

  return settings.reduce((highest, current) => {
    const highestPriority = SCOPE_PRIORITY[highest.scope];
    const currentPriority = SCOPE_PRIORITY[current.scope];

    return currentPriority > highestPriority ? current : highest;
  }, settings[0]);
};

interface SettingAction {
  type: string;
  payload: string;
}

export interface SettingState {
  settings: Setting[];
  isFetching: boolean;
  hasError: boolean;
  errorMessage?: string;
}

const initialStates: SettingState = {
  settings: [],
  isFetching: false,
  hasError: false,
};

const setting: Reducer<SettingState, SettingAction> = (
  state = initialStates,
  action
) => {
  switch (action.type) {
    case FETCH_SETTING_REQUEST: {
      return {
        ...state,
        isFetching: true,
      };
    }
    case CREATE_USER_SETTING_COMMIT: {
      const newSetting = action.payload as unknown as Setting;
      return {
        ...state,
        settings: [...state.settings, newSetting],
        isFetching: false,
        hasError: false,
      };
    }
    case UPDATE_USER_SETTING_COMMIT: {
      const updatedSetting = action.payload as unknown as Setting;

      const updatedSettings = state.settings.map((setting) => {
        if (setting.settingId === updatedSetting.settingId) {
          return updatedSetting;
        }
        return setting;
      });
      return {
        ...state,
        settings: updatedSettings,
        isFetching: false,
        hasError: false,
      };
    }
    case FETCH_SETTING_COMMIT: {
      const payload: Setting[] = (
        (action.payload as unknown as Setting[]) ?? []
      ).filter(({ couldNotBeLoaded }) => !couldNotBeLoaded);

      const settingKeyMap: Record<string, Setting[]> = payload.reduce(
        (acc, setting) => {
          const key = setting.key;
          if (!acc[key]) {
            acc[key] = [];
          }
          acc[key].push(setting);
          return acc;
        },
        {} as Record<string, Setting[]>
      );

      const settings = Object.values(settingKeyMap).reduce((acc, settings) => {
        const settingWithHighestPriority = getSettingWithHighestPriority(
          distinctByProperty(settings, 'settingId')
        );
        if (settingWithHighestPriority) {
          acc.push(settingWithHighestPriority);
        }
        return acc;
      }, [] as Setting[]);

      return {
        ...state,
        settings,
        isFetching: false,
        hasError: false,
      };
    }
    case FETCH_SETTING_ROLLBACK: {
      return {
        ...state,
        isFetching: false,
        hasError: true,
        errorMessage: 'settings:errorMessages.fetchSettingsError',
      };
    }
    case CREATE_USER_SETTING_ROLLBACK:
    case UPDATE_USER_SETTING_ROLLBACK: {
      return {
        ...state,
        isFetching: false,
        hasError: true,
        errorMessage: 'settings:errorMessages.updateSettingsError',
      };
    }

    case CLEAR_PRIO_CACHE: {
      return initialStates;
    }
    default:
      return state;
  }
};
export default setting;

export const getSettingsByKey = (
  state: SettingState,
  settingsKey: string
): Setting[] => state.settings?.filter((s) => s.key === settingsKey);

export const getIsSettingEnabled = (
  state: SettingState,
  key: string
): boolean => {
  const setting = state.settings?.find((s) => s.key === key);
  if (!setting) {
    return false;
  }
  const parsedValue = JSON.parse(setting?.jsonValue || '{}');
  return parsedValue?.isEnabled || false;
};

export const getPhoneLinePreference = (
  state: SettingState
): PhoneLinePreference => {
  const phoneLinePreferenceSettings = state.settings?.filter(
    (s) => s.key === 'General_PhoneLinePreference'
  );

  const userPhoneLinePreference =
    phoneLinePreferenceSettings.find((setting) => setting.scope === 'user') ??
    null;

  const genericSetting = state.settings?.find(
    (s) => s.key === 'General_PhoneLinePreference'
  );
  const parsedValue = JSON.parse(
    (userPhoneLinePreference || genericSetting)?.jsonValue || '{}'
  );

  const phoneLinePreference = Object.keys(parsedValue).find(
    (key) => parsedValue[key] === true
  ) as PhoneLinePreference;
  return phoneLinePreference;
};

export const getIsFetchingAiSetting: (state: SettingState) => boolean = (
  state
) => state?.isFetching;

export const getHasErrorAiSetting: (state: SettingState) => boolean = (state) =>
  state?.hasError;

export const getErrorMessageAiSetting: (state: SettingState) => string = (
  state
) => state?.errorMessage;

export interface ExtendedTrackingSettings {
  isEnabled: boolean;
  replayEnabled: boolean;
  userIdTracing: boolean;
}

export const getSentryExtendedTrackingSetting = (
  state: SettingState
): ExtendedTrackingSettings => {
  const setting = state.settings?.find(
    (s) => s.key === 'General_EnhancedLogging'
  );
  if (!setting) {
    return {
      isEnabled: false,
      replayEnabled: false,
      userIdTracing: false,
    };
  }
  const parsedValue = JSON.parse(setting?.jsonValue || '{}');
  return {
    isEnabled: parsedValue?.isEnabled || false,
    replayEnabled: parsedValue?.isReplayEnabled || false,
    userIdTracing: parsedValue?.isUserIdTracingEnabled || false,
  };
};

export const getSentryEnhancedDiagnosticsSetting = (
  state: SettingState
): boolean => {
  const setting = state.settings?.find(
    (s) => s.key === 'Sentry_EnhancedDiagnostics'
  );
  if (!setting) {
    return false;
  }
  const parsedValue = JSON.parse(setting?.jsonValue || '{}');
  return parsedValue?.isEnabled || false;
};
